/**
 * A D3-powered 100% stacked interval timeline with proportional UTC dates and explicit gaps.
 * @element qds-stacked-timeline
 * @attr {string} title-text - Chart heading.
 * @attr {string} description - Additional chart context.
 * @attr {number} height - Plot height in CSS pixels, from 240 to 800; default 320.
 * @attr {boolean} loading - Show the loading state.
 * @attr {string} error - Show an application error instead of stale data.
 * @property {Array} series - Series objects containing stable id, label, and optional CSS color.
 * @property {Array} periods - Nonoverlapping periods: id, start, exclusive end, values, optional label/state/displayValues.
 * @property {Object} viewRange - Visible UTC date range with start and exclusive end, or null for the full range.
 * @property {string} selectedPeriodId - Selected period ID; selection never changes financial data.
 * @property {string} activeSeriesId - Highlighted series ID; other series remain in the 100% total.
 * @fires {CustomEvent} qds-period-select - A period was activated; detail has periodId, start, end, missing, and seriesId.
 * @fires {CustomEvent} qds-range-change - Range controls changed the viewport; detail has start, end, and source.
 * @fires {CustomEvent} qds-series-focus - Legend highlight changed; detail has seriesId or null.
 * @csspart frame - Shared chart frame; nested parts are exported as chart-* names.
 * @csspart plot - Responsive SVG plot.
 * @csspart controls - Zoom, pan, and reset controls.
 * @csspart details - Selected period's accessible values.
 */
import { scaleLinear, scaleUtc } from 'd3-scale';
import { loadBaseCSS } from '../shared/button-utils.js';
import '../chart/qds-chart.js';
import type { QdsChart } from '../chart/qds-chart.js';
import { chartColor, type QdsChartSeries } from '../chart/chart-colors.js';
import { layoutTimeline, timelineDate, timelineDateText, type QdsTimelinePeriod, type QdsTimelineRange, type TimelineInterval } from './timeline-layout.js';

const day = 86400000;
const svgNamespace = 'http://www.w3.org/2000/svg';
function svgElement<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}, text?: string): SVGElementTagNameMap[K] {
  const node = document.createElementNS(svgNamespace, tag);
  Object.entries(attrs).forEach(([name, value]) => node.setAttribute(name, String(value)));
  if (text !== undefined) node.textContent = text;
  return node;
}

export class QdsStackedTimeline extends HTMLElement {
  private _series: QdsChartSeries[] = [];
  private _periods: QdsTimelinePeriod[] = [];
  private _layout: ReturnType<typeof layoutTimeline> | null = null;
  private _viewport: [number, number] | null = null;
  private _selectedPeriodId = '';
  private _activeSeriesId = '';
  private _focusPeriodId = '';
  private _width = 800;
  private _frame!: QdsChart;
  private _svg!: SVGSVGElement;
  private _details!: HTMLElement;
  private _rangeLabel!: HTMLElement;

  static get observedAttributes(): string[] { return ['title-text', 'description', 'height', 'loading', 'error']; }
  constructor() { super(); this.attachShadow({ mode: 'open' }); loadBaseCSS(); }
  get series(): QdsChartSeries[] { return structuredClone(this._series); }
  set series(value: QdsChartSeries[]) { this._series = structuredClone(value); this._render(); }
  get periods(): QdsTimelinePeriod[] { return structuredClone(this._periods); }
  set periods(value: QdsTimelinePeriod[]) { this._periods = structuredClone(value); this._render(); }
  get viewRange(): QdsTimelineRange | null { return this._viewport ? { start: timelineDateText(this._viewport[0]), end: timelineDateText(this._viewport[1]) } : null; }
  set viewRange(value: QdsTimelineRange | null) {
    if (value) {
      const start = timelineDate(value.start), end = timelineDate(value.end);
      if (end <= start) throw new Error('View range must end after it starts.');
      this._viewport = [start, end];
    } else this._viewport = null;
    this._render();
  }
  get selectedPeriodId(): string { return this._selectedPeriodId; }
  set selectedPeriodId(value: string) { this._selectedPeriodId = String(value || ''); this._renderSelection(); }
  get activeSeriesId(): string { return this._activeSeriesId; }
  set activeSeriesId(value: string) { this._activeSeriesId = String(value || ''); if (this._frame) this._frame.activeSeriesId = this._activeSeriesId; this._applyHighlight(); }

  connectedCallback(): void {
    this._build(); this._render();
    this.shadowRoot!.addEventListener('click', this._onClick);
    this.shadowRoot!.addEventListener('keydown', this._onKeyDown);
    this.shadowRoot!.addEventListener('focusin', this._onPreview);
    this.shadowRoot!.addEventListener('pointerover', this._onPreview);
    this.shadowRoot!.addEventListener('qds-chart-resize', this._onResize);
    this.shadowRoot!.addEventListener('qds-series-focus', this._onSeriesFocus);
    this._svg.addEventListener('pointerleave', this._onPointerLeave);
  }
  disconnectedCallback(): void {
    this.shadowRoot!.removeEventListener('click', this._onClick);
    this.shadowRoot!.removeEventListener('keydown', this._onKeyDown);
    this.shadowRoot!.removeEventListener('focusin', this._onPreview);
    this.shadowRoot!.removeEventListener('pointerover', this._onPreview);
    this.shadowRoot!.removeEventListener('qds-chart-resize', this._onResize);
    this.shadowRoot!.removeEventListener('qds-series-focus', this._onSeriesFocus);
    this._svg.removeEventListener('pointerleave', this._onPointerLeave);
  }
  attributeChangedCallback(): void { this._render(); }

  private _build(): void {
    if (this._frame) return;
    this.shadowRoot!.innerHTML = `<style>
      :host { display:block; min-width:0; }
      svg { display:block; width:100%; overflow:visible; font:12px var(--theme-font-family,'IBM Plex Sans',sans-serif); }
      svg text { fill:var(--theme-text-secondary,#555); }
      .axis { stroke:var(--theme-border-primary,#e0e0e0); }
      .segment { transition:opacity .2s ease; }
      .outline { fill:transparent; stroke:transparent; stroke-width:2; pointer-events:none; }
      g[role="button"] { cursor:pointer; }
      g[role="button"]:focus { outline:none; }
      g[role="button"]:focus-visible .outline, g[aria-pressed="true"] .outline { stroke:var(--theme-text-primary,#333); }
      [part="controls"] { display:flex; align-items:center; flex-wrap:wrap; gap:var(--theme-space-2,8px); margin-block:var(--theme-space-3,12px); }
      button { min-height:44px; border:1px solid var(--theme-border-primary,#e0e0e0); border-radius:var(--theme-radius-sm,4px); padding:8px 12px; background:var(--theme-background-primary,#fff); color:var(--theme-text-primary,#333); font:inherit; cursor:pointer; }
      button:hover:enabled { background:var(--theme-background-hover,rgba(0,0,0,.05)); }
      button:disabled { opacity:.5; cursor:default; }
      button:focus-visible { outline:2px solid var(--theme-accent-primary,#445E89); outline-offset:2px; }
      [part="details"] { font-variant-numeric:tabular-nums; }
      [part="details"] ul { display:flex; flex-wrap:wrap; gap:8px 24px; padding-inline-start:20px; }
      @media (prefers-reduced-motion:reduce) { .segment { transition:none; } }
    </style>
    <qds-chart part="frame" exportparts="container:chart-container,heading:chart-heading,description:chart-description,legend:chart-legend,tooltip:chart-tooltip,status:chart-status">
      <div slot="tools" part="controls" role="group" aria-label="Timeline range controls">
        <button type="button" data-action="earlier" aria-label="Show earlier dates">Earlier</button>
        <button type="button" data-action="zoom-in">Zoom in</button>
        <button type="button" data-action="zoom-out">Zoom out</button>
        <button type="button" data-action="later" aria-label="Show later dates">Later</button>
        <button type="button" data-action="reset">Reset range</button><span id="range-label" role="status"></span>
      </div>
      <svg slot="plot" part="plot" role="group" aria-label="Percentage shares over time"></svg>
      <div slot="details" part="details" aria-live="polite"></div>
    </qds-chart>`;
    this._frame = this.shadowRoot!.querySelector('qds-chart')!;
    this._svg = this.shadowRoot!.querySelector('svg')!;
    this._details = this.shadowRoot!.querySelector('[part="details"]')!;
    this._rangeLabel = this.shadowRoot!.querySelector('#range-label')!;
  }
  private _render(): void {
    if (!this.isConnected || !this._frame) return;
    this._frame.setAttribute('title-text', this.getAttribute('title-text') || 'Share over time');
    this._frame.setAttribute('description', `${this.getAttribute('description') || ''} Dates are proportional. Gaps show unavailable coverage. Use arrow keys between periods; Enter or Space selects a period.`.trim());
    this._frame.toggleAttribute('loading', this.hasAttribute('loading'));
    this._frame.setAttribute('error', this.getAttribute('error') || '');
    this._frame.tooltip = null;
    try {
      this._layout = layoutTimeline(this._series, this._periods);
      this._frame.series = this._layout.series;
      this._frame.setAttribute('empty-message', this._layout.periods.length ? '' : 'No timeline periods to display.');
      this._drawPlot(); this._renderSelection();
    } catch (error) {
      this._layout = null; this._svg.replaceChildren(); this._details.replaceChildren();
      this._frame.setAttribute('error', error instanceof Error ? error.message : 'Timeline data is invalid.');
    }
  }
  private _domain(): [number, number] | null {
    const periods = this._layout?.periods;
    return periods?.length ? [periods[0].startTime, periods[periods.length - 1].endTime] : null;
  }
  private _drawPlot(): void {
    const domain = this._domain(); this._svg.replaceChildren(); if (!domain) return;
    const requested = this._viewport || domain;
    const span = Math.min(domain[1] - domain[0], Math.max(day, requested[1] - requested[0]));
    const start = Math.max(domain[0], Math.min(requested[0], domain[1] - span));
    this._viewport = [start, start + span];
    const width = Math.max(280, this._width), height = Math.min(800, Math.max(240, Number(this.getAttribute('height')) || 320));
    const left = 48, right = width - 20, top = 20, bottom = height - 48;
    const x = scaleUtc().domain(this._viewport.map(time => new Date(time))).range([left, right]);
    const y = scaleLinear().domain([0, 1]).range([bottom, top]);
    this._svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    this._svg.setAttribute('height', String(height));
    this._svg.append(svgElement('desc', {}, 'Each period holds its values constant until its exclusive end date. Missing and zero-total periods cannot be allocated.'));
    [0, .25, .5, .75, 1].forEach(share => {
      this._svg.append(svgElement('line', { x1: left, x2: right, y1: y(share), y2: y(share), class: 'axis' }),
        svgElement('text', { x: left - 8, y: y(share) + 4, 'text-anchor': 'end' }, `${share * 100}%`));
    });
    this._svg.append(svgElement('rect', { x: left, y: top, width: right - left, height: bottom - top, fill: 'var(--theme-background-secondary,#f8f8f8)' }));
    const periods = this._layout!.periods.filter(period => period.endTime > this._viewport![0] && period.startTime < this._viewport![1]);
    if (!periods.some(period => period.id === this._focusPeriodId)) this._focusPeriodId = periods.find(period => period.id === this._selectedPeriodId)?.id || periods[0]?.id || '';
    periods.forEach(period => {
      const x1 = x(new Date(Math.max(period.startTime, this._viewport![0]))), x2 = x(new Date(Math.min(period.endTime, this._viewport![1])));
      const group = svgElement('g', { role: 'button', 'data-period': period.id, tabindex: period.id === this._focusPeriodId ? 0 : -1,
        'aria-pressed': String(period.id === this._selectedPeriodId), 'aria-label': `${period.label || period.start} to ${period.end} (exclusive)${period.missing ? ', unavailable values' : ''}` });
      group.append(svgElement('title', {}, this._periodLines(period).join('\n')));
      if (period.missing) {
        group.append(svgElement('rect', { x: x1, y: top, width: x2 - x1, height: bottom - top, fill: 'var(--theme-background-warning-subtle,rgba(255,163,0,.16))', stroke: 'var(--theme-warning,#FFA300)', 'stroke-dasharray': '4 3' }));
        if (x2 - x1 > 70) group.append(svgElement('text', { x: (x1 + x2) / 2, y: (top + bottom) / 2, 'text-anchor': 'middle' }, 'Unavailable'));
      } else period.segments.forEach(segment => {
        if (segment.to <= segment.from) return;
        const series = this._layout!.series.find(item => item.id === segment.seriesId)!;
        group.append(svgElement('rect', { class: 'segment', 'data-series-id': series.id, x: x1, y: y(segment.to), width: x2 - x1, height: y(segment.from) - y(segment.to), fill: chartColor(series) }));
      });
      group.append(svgElement('rect', { class: 'outline', x: x1 + 1, y: top + 1, width: Math.max(0, x2 - x1 - 2), height: bottom - top - 2 }));
      this._svg.append(group);
    });
    const ticks = x.ticks(Math.max(2, Math.floor((right - left) / 110)));
    const formatter = x.tickFormat(Math.max(2, ticks.length), '%d %b %Y');
    ticks.forEach(date => this._svg.append(svgElement('text', { x: x(date), y: bottom + 24, 'text-anchor': 'middle' }, formatter(date))));
    this._rangeLabel.textContent = `${timelineDateText(this._viewport[0])} to ${timelineDateText(this._viewport[1])} (end exclusive)`;
    const disabled: Record<string, boolean> = { earlier: start <= domain[0], later: start + span >= domain[1], 'zoom-in': span <= day,
      'zoom-out': span >= domain[1] - domain[0], reset: start === domain[0] && start + span === domain[1] };
    this.shadowRoot!.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach(button => button.disabled = disabled[button.dataset.action!] || false);
    this._applyHighlight();
  }
  private _periodLines(period: TimelineInterval): string[] {
    return period.missing ? ['Unavailable: missing details or zero total.'] : period.segments.map(segment => {
      const series = this._layout!.series.find(item => item.id === segment.seriesId)!;
      return `${series.label}: ${period.displayValues?.[series.id] ?? String(segment.value)} · ${((segment.to - segment.from) * 100).toFixed(6)}%`;
    });
  }
  private _renderSelection(): void {
    if (!this._details) return;
    this._details.replaceChildren();
    this._svg.querySelectorAll<SVGGElement>('[data-period]').forEach(group => group.setAttribute('aria-pressed', String(group.dataset.period === this._selectedPeriodId)));
    const period = this._layout?.periods.find(item => item.id === this._selectedPeriodId); if (!period) return;
    const heading = document.createElement('h3'); heading.textContent = `${period.label || period.start} — ${period.end} (exclusive)`;
    const list = document.createElement('ul'); this._periodLines(period).forEach(line => { const item = document.createElement('li'); item.textContent = line; list.append(item); });
    this._details.append(heading, list);
  }
  private _applyHighlight(): void {
    this._svg?.querySelectorAll<SVGElement>('[data-series-id]').forEach(node => node.style.opacity = !this._activeSeriesId || node.dataset.seriesId === this._activeSeriesId ? '1' : '.25');
  }
  private _periodTarget(event: Event): SVGGElement | null { return (event.target as Element).closest<SVGGElement>('g[data-period]'); }
  private _onClick = (event: Event) => {
    const control = (event.target as Element).closest<HTMLButtonElement>('button[data-action]');
    if (control) { this._changeRange(control.dataset.action!); return; }
    const target = this._periodTarget(event); if (!target) return;
    this._select(target.dataset.period!, (event.target as SVGElement).dataset.seriesId || null);
  };
  private _select(id: string, seriesId: string | null): void {
    const period = this._layout?.periods.find(item => item.id === id); if (!period) return;
    this._selectedPeriodId = id; this._frame.tooltip = null; this._renderSelection();
    this.dispatchEvent(new CustomEvent('qds-period-select', { bubbles: true, composed: true, detail: { periodId: id, start: period.start, end: period.end, missing: period.missing, seriesId } }));
  }
  private _onPreview = (event: Event) => {
    const group = this._periodTarget(event); if (!group) return;
    const period = this._layout?.periods.find(item => item.id === group.dataset.period); if (!period) return;
    if (period.id === this._selectedPeriodId) { this._frame.tooltip = null; return; }
    this._frame.tooltip = { title: `${period.label || period.start} — ${period.end} (exclusive)`, lines: this._periodLines(period) };
  };
  private _onPointerLeave = () => { this._frame.tooltip = null; };
  private _onResize = (event: Event) => {
    const width = Math.floor((event as CustomEvent<{ width: number }>).detail.width);
    if (width > 0 && width !== this._width) { this._width = width; this._drawPlot(); }
  };
  private _onSeriesFocus = (event: Event) => { this.activeSeriesId = (event as CustomEvent<{ seriesId: string | null }>).detail.seriesId || ''; };
  private _onKeyDown = (event: Event) => {
    const key = event as KeyboardEvent, group = this._periodTarget(event); if (!group) return;
    const groups = [...this._svg.querySelectorAll<SVGGElement>('[data-period]')];
    const index = groups.indexOf(group);
    const next = key.key === 'ArrowRight' ? index + 1 : key.key === 'ArrowLeft' ? index - 1 : key.key === 'Home' ? 0 : key.key === 'End' ? groups.length - 1 : -1;
    if (next >= 0 && next < groups.length) {
      event.preventDefault(); groups.forEach(item => item.setAttribute('tabindex', '-1'));
      this._focusPeriodId = groups[next].dataset.period!; groups[next].setAttribute('tabindex', '0'); groups[next].focus();
    } else if (key.key === 'Enter' || key.key === ' ') { event.preventDefault(); this._select(group.dataset.period!, null); }
  };
  private _changeRange(action: string): void {
    const domain = this._domain(); if (!domain || !this._viewport) return;
    const span = this._viewport[1] - this._viewport[0], center = (this._viewport[0] + this._viewport[1]) / 2;
    const nextSpan = action === 'zoom-in' ? Math.max(day, Math.round(span / 2 / day) * day) : action === 'zoom-out' ? Math.min(domain[1] - domain[0], span * 2) : span;
    const nextStart = action === 'earlier' ? this._viewport[0] - Math.max(day, Math.round(span / 2 / day) * day)
      : action === 'later' ? this._viewport[0] + Math.max(day, Math.round(span / 2 / day) * day) : Math.round((center - nextSpan / 2) / day) * day;
    this._viewport = action === 'reset' ? domain : [nextStart, nextStart + nextSpan];
    this._drawPlot(); this._frame.tooltip = null;
    this.dispatchEvent(new CustomEvent('qds-range-change', { bubbles: true, composed: true, detail: { ...this.viewRange, source: action } }));
  }
}
if (!customElements.get('qds-stacked-timeline')) customElements.define('qds-stacked-timeline', QdsStackedTimeline);
